import React, { useState } from 'react';
import { FaPhone, FaFileAlt, FaShieldAlt, FaEnvelope } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './Claims.css';

// ✅ Correct imports
import { db } from '../utils/firebase';
import { collection, addDoc } from 'firebase/firestore';
import emailjs from '@emailjs/browser';

const Claims = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    mobile: '',
    email: '',
    policyNumber: '',
    insuranceCompany: '',
    claimType: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const steps = [
    {
      icon: <FaPhone />,
      title: 'Step 1 – Contact Us',
      description: 'Call or message us with your policy details.'
    },
    {
      icon: <FaFileAlt />,
      title: 'Step 2 – Submit Documents',
      description: 'We guide you with required documents & inspection.'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Step 3 – Claim Settlement',
      description: 'We coordinate with insurer for smooth & quick settlement.'
    }
  ];

  const insuranceCompanies = [
    'Tata AIG',
    'Go Digit',
    'Bajaj Allianz',
    'ICICI Lombard',
    'HDFC ERGO'
  ];

  const claimTypes = [
    'Accident Claim',
    'Theft Claim',
    'Damage Claim',
    'Third-Party Claim'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // ✅ Save to Firestore
      await addDoc(collection(db, 'claims'), {
        ...formData,
        timestamp: new Date().toISOString()
      });

      // ✅ Send email via EmailJS
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_claim',
        {
          from_name: formData.fullName,
          from_email: formData.email,
          mobile: formData.mobile,
          policy_number: formData.policyNumber,
          insurance_company: formData.insuranceCompany,
          claim_type: formData.claimType,
          message: formData.message,
          to_email: 'insurance@bereliable.in'
        }
      );

      toast.success('Claim request submitted successfully! We will contact you shortly.');

      setFormData({
        fullName: '',
        mobile: '',
        email: '',
        policyNumber: '',
        insuranceCompany: '',
        claimType: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting claim:', error);
      toast.error('Failed to submit claim request. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="claims-page">
      
      {/* SECTION 1 – HERO */}
      <section className="claims-hero">
        <div className="container">
          <div className="claims-hero-content">
            <div className="claims-hero-left">
              <h1 className="claims-hero-title">Claim Assistance</h1>
              <p className="claims-hero-subtitle">Fast, Transparent & Hassle-Free Claim Support</p>
            </div>
            <div className="claims-hero-right">
              <div className="claims-hero-illustration">
                <FaShieldAlt className="claims-illustration-icon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 – HOW CLAIM WORKS */}
      <section className="claims-process">
        <div className="container">
          <h2 className="section-title">How Claim Works</h2>
          <div className="claims-steps-grid">
            {steps.map((step, index) => (
              <div key={index} className="claim-step-card card fade-in">
                <div className="claim-step-icon">{step.icon}</div>
                <h3 className="claim-step-title">{step.title}</h3>
                <p className="claim-step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 – CLAIM ASSISTANCE FORM */}
      <section className="claims-form-section">
        <div className="container">
          <div className="claims-form-wrapper card">
            <h2 className="claims-form-title">Claim Assistance Form</h2>

            <form onSubmit={handleSubmit} className="claims-form">

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    className="form-input"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Mobile Number *</label>
                  <input
                    type="tel"
                    name="mobile"
                    className="form-input"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Policy Number *</label>
                  <input
                    type="text"
                    name="policyNumber"
                    className="form-input"
                    value={formData.policyNumber}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Insurance Company *</label>
                  <select
                    name="insuranceCompany"
                    className="form-select"
                    value={formData.insuranceCompany}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Insurance Company</option>
                    {insuranceCompanies.map((company) => (
                      <option key={company} value={company}>
                        {company}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Claim Type *</label>
                  <select
                    name="claimType"
                    className="form-select"
                    value={formData.claimType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Claim Type</option>
                    {claimTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  name="message"
                  className="form-textarea"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Please provide details about your claim..."
                ></textarea>
              </div>

              <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Claim Request'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* SECTION 4 – SUPPORT DETAILS */}
      <section className="claims-support">
        <div className="container">
          <div className="support-cards-grid">
            <div className="support-card card">
              <div className="support-card-icon">
                <FaPhone />
              </div>
              <h3 className="support-card-title">24/7 Assistance</h3>
              <p className="support-card-text">Phone: +91 70417 11159, +91 88661 41159</p>
            </div>

            <div className="support-card card">
              <div className="support-card-icon">
                <FaEnvelope />
              </div>
              <h3 className="support-card-title">Email Support</h3>
              <p className="support-card-text">insurance@bereliable.in</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Claims;
